import React, { useState, useEffect, useMemo, useRef } from 'react';
import DataTablesSurveyList from '../../../../component/common/DataTablesSurveyList';
import { Link } from "react-router-dom";
import SearchInput from '../../../../core/Inputs/SearchInput';
import Svg from '../../../../component/common/Svg';
import { useDispatch } from 'react-redux';
import { getTokenData } from '../../../../store/actions/authAction';
import { useTranslation } from 'react-i18next';
import { getLanguages } from "../../../../store/actions/surveyAction";
import { useSelector } from "react-redux";
import { getSurveyGroups, addSurveyToGroup } from '../../../../store/actions/surveyGroupAction';
import { toast } from 'react-toastify';
import { apiRequest } from "../../../../services/apiService";
import Card from "../../../../core/Card";
import style from "../../../style.module.css";
export default function Index() {

  //States
  const initialFilters = { search: '', status: '', language: '' }
  const { t, i18n } = useTranslation();
  const surveyStatus = useMemo(
    () => [
      {
        name: t('survey.published'),
        value: 1,
      },
      {
        name: t('survey.draft'),
        value: 2,
      },
    ],
    [i18n.language]
  );

  const [userId, setuserId] = useState('');
  const [filters, setFilters] = useState(initialFilters);
  const [status, setStatus] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(t('survey.all'));
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isAddToGroupOpen, setIsAddToGroupOpen] = useState(false);
  const [isCreateNewOpen, setIsCreateNewOpen] = useState(false);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const addToGroupRef = useRef(null);
  const createNewRef = useRef(null);
  const dispatch = useDispatch()
  const { languageList } = useSelector((state) => state.surveyData);
  const { groupList, groupListLoading } = useSelector((state) => state.surveyGroupData);

  // Sync From UAT (Secondary DB)
  const [refreshKey, setRefreshKey] = useState(0);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [uatSurveys, setUatSurveys] = useState([]);
  const [uatLoading, setUatLoading] = useState(false);
  const [uatSelectedSurveyId, setUatSelectedSurveyId] = useState(null);
  const [uatSelectedSurveyLang, setUatSelectedSurveyLang] = useState(null);
  const [uatSyncing, setUatSyncing] = useState(false);

  const resetSyncModal = () => {
    setIsSyncModalOpen(false);
    setUatSurveys([]);
    setUatLoading(false);
    setUatSelectedSurveyId(null);
    setUatSelectedSurveyLang(null);
    setUatSyncing(false);
  };

  const getSurveyNameForRow = (row) => {
    try {
      const nameObj = row?.survey_name ? JSON.parse(row.survey_name) : {};
      const langCode = row?.su_languages?.toString()?.split(',')?.[0]?.trim();
      return nameObj?.[langCode] || Object.values(nameObj || {})[0] || 'Draft_Survey';
    } catch (e) {
      return 'Draft_Survey';
    }
  };

  const openSyncModal = async () => {
    setIsSyncModalOpen(true);
    setUatLoading(true);
    setUatSurveys([]);
    setUatSelectedSurveyId(null);
    setUatSelectedSurveyLang(null);

    const { response, error } = await apiRequest({
      method: 'GET',
      url: `/survey/getSecondarySurveyList?limit=200&offset=0`,
      data: null,
      showErrorToast: false,
    });

    if (response) {
      setUatSurveys(response?.data?.data || []);
    } else {
      const msg = error?.response?.data?.msg || t('sync.toast.fetchUatSurveysFailed');
      toast.error(msg);
    }
    setUatLoading(false);
  };

  const handleSyncSelectedSurvey = async () => {
    if (!uatSelectedSurveyId) {
      toast.error(t('sync.toast.selectSurveyToSync'));
      return;
    }
    setUatSyncing(true);
    const { response, error } = await apiRequest({
      method: 'POST',
      url: `/survey/syncSurveyFromUAT`,
      data: {
        survey_id: uatSelectedSurveyId,
        language_code: uatSelectedSurveyLang || null,
      },
      showErrorToast: false,
    });

    if (response) {
      toast.success(response?.data?.msg || t('sync.toast.surveySyncedSuccess'));
      setRefreshKey((k) => k + 1);
      resetSyncModal();
    } else {
      const msg = error?.response?.data?.msg || t('sync.toast.fullReplacementFailed');
      toast.error(msg);
      setUatSyncing(false);
    }
  };

  // Map groupList to dropdown format
  const groupsList = useMemo(() => {
    if (!groupList || groupList.length === 0) return [];
    return groupList.map(group => ({
      id: group.id,
      name: group.name
    }));
  }, [groupList]);

  // Filter groups based on search query
  const filteredGroupsList = useMemo(() => {
    if (!groupSearchQuery.trim()) return groupsList;
    return groupsList.filter(group => 
      group.name.toLowerCase().includes(groupSearchQuery.toLowerCase())
    );
  }, [groupsList, groupSearchQuery]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (addToGroupRef.current && !addToGroupRef.current.contains(event.target)) {
        setIsAddToGroupOpen(false);
        setSelectedGroup(null);
        setGroupSearchQuery('');
      }
      if (createNewRef.current && !createNewRef.current.contains(event.target)) {
        setIsCreateNewOpen(false);
      }
    };

    if (isAddToGroupOpen || isCreateNewOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isAddToGroupOpen, isCreateNewOpen]);

  // Handle cancel button click for Add to Group
  const handleCancelAddToGroup = () => {
    setIsAddToGroupOpen(false);
    setSelectedGroup(null);
    setGroupSearchQuery('');
  };

  const handleDirectAddToGroup = async () => {
    if (!selectedGroup || !selectedGroup.id) {
      toast.error(t('sync.toast.noGroupSelected'));
      return;
    }

    if (!selectedRows || selectedRows.length === 0) {
      toast.error(t('sync.toast.noSurveysSelectedToAdd'));
      return;
    }

    const results = {
      success: 0,
      errors: [],
      languageConflicts: [],
    };

    // Add each selected survey to the group
    for (const selectedRow of selectedRows) {
      const survey = selectedRow;
      
      if (!survey) {
        results.errors.push(`Survey not found`);
        continue;
      }

      // Get survey name for better error messages
      let surveyName = 'Unknown Survey';
      try {
        const surveyNameObj = survey.survey_name ? JSON.parse(survey.survey_name) : {};
        const languageCode = survey.su_languages?.split(',')[0];
        surveyName = surveyNameObj[languageCode] || Object.values(surveyNameObj)[0] || 'Unknown Survey';
      } catch (e) {
        // Use fallback name
      }

      // Get language code - use the first language from su_languages
      const languageCode = survey.su_languages ? survey.su_languages.toString().split(',')[0].trim() : '';
      
      if (!languageCode) {
        results.errors.push(`${surveyName}: No language code`);
        continue;
      }

      // Prepare payload for this survey
      const payload = {
        group_id: selectedGroup.id,
        survey_id: survey.survey_id,
        language_code: languageCode,
        sort_order: null, // Backend will calculate
        is_primary: 0 // New surveys are not primary by default
      };

      try {
        await new Promise((resolve, reject) => {
          dispatch(
            addSurveyToGroup(
              payload,
              () => {
                results.success++;
                resolve();
              },
              (error) => {
                const msg = error.response?.data?.msg || 'Failed to add survey';
                
                // Check if it's a language conflict
                if (msg.toLowerCase().includes('language already exists') || 
                    (msg.toLowerCase().includes('language') && msg.toLowerCase().includes('group'))) {
                    results.languageConflicts.push(`${surveyName} (${languageCode})`);
                } else {
                    results.errors.push(`${surveyName}: ${msg}`);
                }
                reject(error);
              }
            )
          );
        });
      } catch (err) {
        // Error already handled in the callback
      }
    }

    // Show results
    if (results.success > 0) {
      toast.success(t('sync.toast.surveysAddedToGroup', { count: results.success, groupName: selectedGroup.name }));
    }
    
    if (results.languageConflicts.length > 0) {
      toast.warning(t('sync.toast.duplicateLanguagesWarning'));
    }
    
    if (results.errors.length > 0) {
      toast.error(`${t('sync.toast.failedToAddSurveys')}: ${results.errors[0] || ''}`);
    }

    // Close dropdown and reset states
    setIsAddToGroupOpen(false);
    setSelectedGroup(null);
    setGroupSearchQuery('');
    setSelectedRows([]); // Clear selected rows
  };

  //Functionlaities
  const handleSearch = (value) => {
    setFilters({ ...filters, search: value })
  }

  const handleClickDropDown = (opt) => {
    if (opt === t('survey.allL')) {
      setFilters({ ...filters, status: '' })
      setStatus(null)
    }
    else {
      setStatus(opt)
      setFilters({ ...filters, status: opt.value })
    }
  }

  const handleClickLangDropDown = (lang) => {
    if (lang === t('survey.all')) {
      // "All" means no filtering by language
      setFilters((prev) => ({ ...prev, language: '' }));
      setSelectedLanguage(t('survey.all'));
    } else {
      // Use su_language_id (or su_language_name) to filter on
      setFilters((prev) => ({ ...prev, language: lang.su_language_id }));
      setSelectedLanguage(lang.su_language_name);
    }
  };


  useEffect(() => {
    console.log("Dispatching getLanguages API...");
    dispatch(getLanguages()); // Fetch languages on mount
    dispatch(getSurveyGroups()); // Fetch groups on mount
  }, [dispatch]);


  useEffect(() => {
    if (!filters.language) {
      setSelectedLanguage(t('survey.all'));
    }
  }, [i18n.language]);

  console.log("getLanguages", languageList);


  //Effects
  useEffect(() => {
    dispatch(getTokenData(setuserId))
  }, [userId, dispatch])

  useEffect(() => {
    if (status) {
      const updatedStatus = surveyStatus.find((opt) => opt.value === status.value);
      if (updatedStatus) {
        setStatus(updatedStatus);
      }
    }
  }, [i18n.language, status, surveyStatus]);




  return (
    <>
      <div className="flex-grow-1 dashboard-page-outer">
        <div className='dashboard-page-wrap'>
          <div className="page-heading d-md-flex align-items-center justify-content-between mb-20">
            <div className='head-lef'>
              {/* <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/admin/survey/list" className="home-item" title={t('survey.title')}>Home</Link></li>
                  <li className="breadcrumb-item active" aria-current="page"><strong>{t('survey.heading1')}</strong></li>
                </ol>
              </nav> */}
              <div className="survey-tabs-nav">
                <Link to="/admin/survey/list" className="survey-tab-link active">
                  {t('survey.heading')}
                </Link>
                <Link to="/admin/survey/groups" className="survey-tab-link">
                   {t('survey.groups.title')}
                </Link>
              </div>
            </div>
            <div className="right-wrap mt-2 mt-md-0 d-flex align-items-center gap-2">
              {/* Delete button - only show when rows are selected */}
              {/* {selectedRows.length > 0 && (
                <button 
                  className="btn btn-bulk-delete"
                  type="button"
                  title="Delete"
                >
                  <Svg name="trash-red" />
                  Delete
                </button>
              )} */}
              
              {/* Add to group button - enabled when rows are selected */}
              <div className="dropdown" ref={addToGroupRef}>
                <button
                  className={`btn btn-add-group ${selectedRows.length === 0 ? 'disabled' : ''}`}
                  type="button"
                  id="addToGroupDropdown"
                  onClick={() => {
                    if (selectedRows.length > 0) {
                      setIsAddToGroupOpen(!isAddToGroupOpen);
                    }
                  }}
                  aria-haspopup="true"
                  aria-expanded={isAddToGroupOpen}
                  disabled={selectedRows.length === 0}
                >
                  <Svg name="plus-red" />
                 {t('survey.groups.addToGroup')}
                </button>
                {isAddToGroupOpen && (
                  <div className="dropdown-menu dropdown-menu-end add-to-group-dropdown show" aria-labelledby="addToGroupDropdown">
                    <div className="dropdown-search">
                      <input 
                        type="text" 
                        className="form-control" 
                        placeholder={t('settings.users_and_teams.users.search')}
                        value={groupSearchQuery}
                        onChange={(e) => setGroupSearchQuery(e.target.value)}
                      />
                    </div>
                    
                    <div className="groups-list">
                      {groupListLoading ? (
                        <div className="dropdown-item text-center text-muted">
                          Loading...
                        </div>
                      ) : filteredGroupsList.length === 0 ? (
                        <div className="dropdown-item text-center text-muted">
                          {groupSearchQuery ? 'No groups found' : 'No groups available'}
                        </div>
                      ) : (
                        filteredGroupsList.map((group) => (
                        <div
                          key={group.id}
                          className={`dropdown-item group-item ${selectedGroup?.id === group.id ? 'selected' : ''}`}
                          onClick={() => setSelectedGroup(group)}
                        >
                          
                          <Svg name="folder-icon" />
                          {group.name}
                          {selectedGroup?.id === group.id && (
                            <span className="checkmark">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M13.3333 4L6 11.3333L2.66667 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </span>
                          )}
                        </div>
                        ))
                      )}
                    </div>
                    
                    <div className="dropdown-actions">
                      <button 
                        className="btn btn-primary add-btn btn-sm" 
                        type="button"
                        onClick={() => {
                          if (selectedGroup) {
                            // Directly add selected surveys to the group
                            handleDirectAddToGroup();
                          }
                        }}
                        disabled={!selectedGroup}
                      >
                        <Svg name="plus" />
                       {t('survey.stages.add')}
                      </button>
                      <button className="btn btn-bordered color-cancel btn-sm ms-2" type="button" onClick={handleCancelAddToGroup}>
                       {t('survey.customVariable.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Create new button with dropdown */}
              {window.modulePermissions.M1.add === 1 && (
                <>
                  {/* Sync from UAT button - Only visible in Production/Live environment */}
                  {process.env.REACT_APP_ENABLE_UAT_SYNC === 'true' && (
                    <button
                      className="btn btn-bordered me-2"
                      type="button"
                      onClick={openSyncModal}
                    >
                      {t('sync.syncFromUat')}
                    </button>
                  )}
                <div className="dropdown">
                  <button
                    className="btn btn-primary add-btn"
                    type="button"
                    id="createNewDropdown"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    <Svg name="plus" />
                   {t('survey.report.new')}
                    <svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 20 20" fill="none">
                      <path className="fill-none" d="M5 7.5L10 12.5L15 7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>

                  </button>
                  <div className="dropdown-menu" aria-labelledby="createNewDropdown">
                    <Link to="/admin/survey/create/start" className="dropdown-item">
                     {t("survey.title")}
                    </Link>
                    <Link to="/admin/survey/groups" className="dropdown-item">
                      {t("survey.groups.title")}
                    </Link>
                  </div>
                </div>
                </>
              )}
            </div>
          </div>

          <div className="table-wrapper-view">
            <div className="search-filter d-md-flex align-items-center surveyfilter">
              <div className="flex-grow-1 search-filter-padding">
                <SearchInput
                  className="custom-search-input"
                  type='def'
                  view='outlined'
                  color='primary'
                  placeholder={t('survey.search')}
                  fill={true}
                  onSearch={handleSearch}
                  onClear={() => setFilters({ ...filters, search: '' })}
                />
              </div>
              <div className="textS d-flex align-items-center ">



                <label className="text-label">{t('survey.language')}:</label>
                <div className="dropdown table-dropdown ques-options me-2">
                  {/* 3. Use `selectedLanguage` in the button label */}
                  <button
                    className="btn dropdown-tgl"
                    type="button"
                    id="quesoptions"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    {selectedLanguage}
                  </button>

                  <div className="dropdown-menu" aria-labelledby="quesoptions">
                    <span
                      className="dropdown-item"
                      onClick={() => handleClickLangDropDown(t('survey.all'))}
                    >
                      {t('survey.all')}
                    </span>
                    {languageList.result?.map((opt, index) => (
                      <span
                        className="dropdown-item"
                        key={index}
                        onClick={() => handleClickLangDropDown(opt)}
                      >
                        {opt.su_language_name}
                      </span>
                    ))}
                  </div>
                </div>



                <label className="text-label"> {t('survey.status')}:</label>
                <div className="dropdown table-dropdown ques-options">
                  <button className="btn dropdown-tgl" type="button" id="quesoptions" data-bs-toggle="dropdown"
                    aria-haspopup="true" aria-expanded="false">
                    {status ? status.name : t('survey.allL')}
                  </button>

                  <div className="dropdown-menu" aria-labelledby="quesoptions">
                    <span className="dropdown-item" onClick={() => handleClickDropDown(t('survey.allL'))}>{t('survey.allL')} </span>
                    {surveyStatus?.map((opt, index) => {
                      return (
                        <span
                          className="dropdown-item"
                          key={index}
                          onClick={() => handleClickDropDown(opt)}
                        > {opt.name} </span>
                      )
                    })}
                  </div>

                </div>




              </div>
            </div>
            <div className="tablePage bg-white">
              <div className="content-table survey-table">
                <DataTablesSurveyList 
                  userId={userId} 
                  filters={filters} 
                  onSelectedRowsChange={setSelectedRows}
                  refreshKey={refreshKey}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sync From UAT Modal */}
      <div
        className={`customvar-modal modal custom-modal fade deleteModal ${isSyncModalOpen ? 'show' : ''}`}
        style={{ display: `${isSyncModalOpen ? 'block' : 'none'}` }}
        id="syncFromUatModal"
        tabIndex={-1}
        aria-labelledby="syncFromUatModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-body">
              <div className={"formcontainer" + " " + style.container}>
                <div className={"formvawrapper delete-modal-wrap" + " " + style.wrapper}>
                  <Card view="raised" className={"formstyle p-0" + " " + style.card} >
                    <div className="delete-body-wrap delete-language" style={{ padding: '24px' }}>
                      <div className="heading" style={{ marginBottom: '20px' }}>
                        <h3 style={{ marginBottom: '8px', fontSize: '20px', fontWeight: '600' }}>
                          {t('sync.syncFromUat')}
                        </h3>
                        <p className="mb-0" style={{ fontSize: '14px', color: '#6c757d' }}>
                          {/* {uatSurveys?.length > 0 ? `${uatSurveys.length} ${uatSurveys.length === 1 ? t('survey.title') : t('survey.heading')} available` : t('survey.select')} */}
                        </p>
                      </div>

                      <div className="mb-3">
                        {uatLoading ? (
                          <div className="text-center" style={{ padding: '40px 0', color: '#6c757d' }}>
                            <div className="spinner-border spinner-border-sm mb-2" role="status" aria-hidden="true"></div>
                            <div>{t('sync.loadingSurveys')}</div>
                          </div>
                        ) : (
                          <div className="table-responsive" style={{ maxHeight: 360, overflow: 'auto', border: '1px solid #e9ecef', borderRadius: '6px' }}>
                            <table className="table table-hover mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                              <tbody>
                                {(uatSurveys || []).map((row, index) => {
                                  const isSelected = uatSelectedSurveyId === row.survey_id;
                                  const isLastItem = index === uatSurveys.length - 1;
                                  
                                  return (
                                    <tr 
                                      key={row.survey_id} 
                                      style={{ 
                                        cursor: 'pointer',
                                        backgroundColor: isSelected ? '#f0f8ff' : 'transparent',
                                        transition: 'background-color 0.2s ease'
                                      }} 
                                      onClick={() => {
                                        setUatSelectedSurveyId(row.survey_id);
                                        const lang = row?.su_languages?.toString()?.split(',')?.[0]?.trim();
                                        setUatSelectedSurveyLang(lang || null);
                                      }}
                                    >
                                      <td 
                                        style={{ 
                                          width: '40px',
                                          border: 'none',
                                          borderBottom: isLastItem ? 'none' : '1px solid #f0f0f0',
                                          padding: '14px 16px',
                                          verticalAlign: 'middle'
                                        }}
                                      >
                                        <input
                                          type="radio"
                                          name="uatSurveySelect"
                                          checked={isSelected}
                                          onChange={() => {
                                            setUatSelectedSurveyId(row.survey_id);
                                            const lang = row?.su_languages?.toString()?.split(',')?.[0]?.trim();
                                            setUatSelectedSurveyLang(lang || null);
                                          }}
                                          style={{ 
                                            cursor: 'pointer',
                                            width: '16px',
                                            height: '16px',
                                            accentColor: '#e30613'
                                          }}
                                        />
                                      </td>
                                      <td 
                                        style={{ 
                                          border: 'none',
                                          borderBottom: isLastItem ? 'none' : '1px solid #f0f0f0',
                                          padding: '14px 16px',
                                          verticalAlign: 'middle'
                                        }}
                                      >
                                        <div style={{ 
                                          fontWeight: isSelected ? '600' : '500',
                                          fontSize: '14px',
                                          color: isSelected ? '#0F2744' : '#495057',
                                          transition: 'all 0.2s ease'
                                        }}>
                                          {getSurveyNameForRow(row)}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {(!uatSurveys || uatSurveys.length === 0) && (
                                  <tr>
                                    <td 
                                      colSpan={2} 
                                      className="text-muted text-center" 
                                      style={{ 
                                        padding: '40px 20px',
                                        border: 'none',
                                        fontSize: '14px'
                                      }}
                                    >
                                      {t('sync.noSurveysFound')}
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>

                      <div 
                        className="button-wrap d-flex justify-content-end" 
                        style={{ 
                          marginTop: '20px',
                          paddingTop: '20px',
                          //borderTop: '1px solid #e9ecef'
                        }}
                      >
                        <button
                          type="button"
                          className="btn btn-bordered"
                          onClick={resetSyncModal}
                          disabled={uatSyncing}
                          style={{ padding: '8px 24px' }}
                        >
                          {t('sync.cancel')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary ms-3"
                          disabled={uatLoading || uatSyncing || !uatSelectedSurveyId}
                          onClick={handleSyncSelectedSurvey}
                          style={{ padding: '8px 24px' }}
                        >
                          {uatSyncing && (
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          )}
                          {uatSyncing ? t('sync.syncing') : t('sync.sync')}
                        </button>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={`modal-backdrop fade ${isSyncModalOpen ? 'show' : ''}`} style={{ display: `${isSyncModalOpen ? 'block' : 'none'}` }}></div>

    </>
  )
}

